import { useNewsCategoryLabels } from '@/context/newsCategoryLabelsContext';
import { getNewsCategoryLabel } from '@/util/storyblok/news-categories';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import type { ReactElement, RefObject, Ref } from 'react';
import { useCallback, useRef, useState } from 'react';
import styles from './ArticlePreview.module.scss';
import {
  setupMouseEnterTimeline,
  setupMouseLeaveTimeline,
  setupTransitionInTimeline,
} from './ArticlePreview.transitions';
import type { ArticlePreviewProps, ArticlePreviewRef } from './ArticlePreview.data';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import { formatDate } from '../../../../util/dates.utils';
import { renderCta } from '../../../../util/cta/cta.template';
import type { CtaRefType } from '../../../../util/cta/cta.data';
import useScrollTrigger from '../../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import useIsomorphicLayoutEffect from '../../../../hooks/useIsomorphicLayoutEffect';
import { isTouch } from '../../../../hooks/useIsDesktopDevice';
import {
  addImageInVideoObject,
  getFallbackVideo,
  hasVideo,
} from '../../../../util/storyblok/data.utils';
import Video from '../../../atoms/video/Video';
import Image from '../../../atoms/image/Image';
import type { ImageRef } from '../../../atoms/image/Image.data';
import type { VideoRef } from '../../../atoms/video/Video.data';
import { cleanMediaQueries } from '../../../../hooks/useDeviceStateTracker';
import { protocolRelativeToAbsoluteUrl } from '../../../../util/url.utils';
import { fadeTo } from '../../../../util/animation/fade/fade';
import { useGlobalStore } from '../../../../stores/global';
import { useCanPlayWebmContext } from '../../../../context/canPlayWebmContext';

export default function ArticlePreview({
  blok,
  date,
  full_slug,
  textOnly,
  ref,
  ...props
}: ArticlePreviewProps & { ref?: Ref<ArticlePreviewRef> }): ReactElement {
  const { locale } = useRouter();
  const categoryLabels = useNewsCategoryLabels();
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const [formattedDate, setFormattedDate] = useState<string | undefined | null>(date);
  const imageRef = useRef<ImageRef | VideoRef>(null);
  const imageHolderRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const dateHolderRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaHolderRef = useRef<HTMLDivElement>(null);
  const globalStore = useGlobalStore();
  const gifRef = useRef<HTMLVideoElement>(null);
  const { canPlayWebm } = useCanPlayWebmContext();

  useIsomorphicLayoutEffect(() => {
    setFormattedDate(formatDate(date, 'D MMM YYYY', locale));
  }, [date, locale]);

  const { inView } = useScrollTrigger({
    trigger: elementRef,
    animation: useSetupTimeline(setupTransitionInTimeline, {
      imageContainerRef,
      elementRef,
    }),
    enableInView: true,
    vars: {
      scrub: true,
    },
  });

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        imageContainerRef,
        dateHolderRef,
        ctaRef,
        ctaHolderRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  const onMouseEnter = useCallback(() => {
    fadeTo(imageContainerRef.current as HTMLDivElement, { to: 0.7, duration: 0.3 });
    hoverTransitionController.transitionIn();
  }, [hoverTransitionController]);

  const onMouseLeave = useCallback(() => {
    fadeTo(imageContainerRef.current as HTMLDivElement, { to: 1, duration: 0.3 });
    hoverTransitionController.transitionOut();
  }, [hoverTransitionController]);

  const { key_image, key_video, thumbnail_video } = blok;

  let src;
  let srcDesktop;
  if (key_image && key_image?.length > 0) {
    src = protocolRelativeToAbsoluteUrl(key_image[0].image_portrait);
    srcDesktop = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);

    if (!src) {
      src = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);
    }
  }

  let media = <></>;

  if ((key_video && hasVideo(key_video)) || (thumbnail_video && hasVideo(thumbnail_video))) {
    media = (
      <Video
        ref={imageRef as RefObject<VideoRef | null>}
        blok={addImageInVideoObject(
          getFallbackVideo(thumbnail_video, key_video),
          srcDesktop || src,
        )}
        objectFit="cover"
        controls={false}
        playButton={false}
        muted
        loop
        openInModal={false}
        playing={inView}
        className={classNames(styles.video, 'abs-fill')}
      />
    );
  } else if (key_image?.[0]?.animated_image?.filename && canPlayWebm) {
    media = (
      <video
        ref={gifRef}
        src={key_image?.[0]?.animated_image.filename}
        autoPlay
        muted
        controls={false}
        playsInline
        loop
        className={classNames(styles.animatedImage)}
      />
    );
  } else if (key_image?.[0] && src && !hasVideo(key_video) && !hasVideo(thumbnail_video)) {
    media = (
      <Image
        ref={imageRef as RefObject<ImageRef | null>}
        src={src}
        srcDesktop={srcDesktop || src}
        layout="fill"
        className={classNames(styles.image, 'abs-fill')}
        sizes={`${cleanMediaQueries.MIN_768} 1200px, ${cleanMediaQueries.MIN_1920} 1680px, 100vw`}
        alt={key_image[0].alt_text}
      />
    );
  }

  return renderCta({
    innerTemplate: (
      <>
        {!textOnly &&
          ((key_image?.[0] && src) ||
            (key_video && hasVideo(key_video)) ||
            (thumbnail_video && hasVideo(thumbnail_video))) && (
            <div ref={imageHolderRef} className={classNames(styles.imageHolder, 'abs-fill')}>
              <div ref={imageContainerRef} className={classNames(styles.imageContainer)}>
                {media}
              </div>
            </div>
          )}

        {blok.category && (
          <Heading
            as="span"
            size="label-large"
            disableTriggerInOnScroll={false}
            className={styles.category}
          >
            {getNewsCategoryLabel(categoryLabels, blok.category)}
          </Heading>
        )}
        {blok.title && (
          <Heading
            as="h5"
            size="heading-03"
            transitionInType={isTouch ? 'splitLineRotateIn' : 'splitLineRotateIn'}
            disableTriggerInOnScroll={false}
            className={styles.articleTitle}
          >
            {blok.title}
          </Heading>
        )}
        <div ref={dateHolderRef} className={classNames(styles.dateHolder)}>
          {date && (
            <Text
              as="p"
              size="body-standard"
              disableTriggerInOnScroll={false}
              className={styles.date}
              transitionInType={isTouch ? 'splitLineRotateIn' : 'splitLineRotateIn'}
            >
              {formattedDate}
            </Text>
          )}

          <div ref={ctaHolderRef} className={classNames(styles.ctaHolder)}>
            <Text
              ref={ctaRef}
              as="p"
              size="body-standard"
              disableTriggerInOnScroll
              className={styles.cta}
              transitionInType="none"
            >
              <span className={classNames(styles.circle)}></span>
              {globalStore.globalSettings.view_article}
            </Text>
          </div>
        </div>
      </>
    ),
    props: {
      ...props,
      onMouseEnter,
      onMouseLeave,
      className: classNames(styles.articlePreview, props.className, {
        [styles.fullbleed]: ['fullbleed', 'center'].includes(props.layout),
        [styles.center]: props.layout === 'center',
        [styles.textOnly]: textOnly,
      }),
      'aria-label': blok.title,
      link: {
        uuid: blok._uid,
        href: full_slug,
      },
    },
    elementRef,
  });
}
